/**
 * Regions and shading rules of a LITS grid: the TypeScript port of
 * `backend/apps/puzzletypes/lits/shading.py`, kept at parity by the shared fixtures, plus the
 * live contradictions, which only the client computes. Pure functions over row strings.
 */
import { DOT, PIECE_SIZE, SHADED, type Cell } from "./types";

export type Shape = "L" | "I" | "T" | "S";

const BASE: Record<Shape, Cell[]> = {
  L: [
    [0, 0],
    [1, 0],
    [2, 0],
    [2, 1],
  ],
  I: [
    [0, 0],
    [0, 1],
    [0, 2],
    [0, 3],
  ],
  T: [
    [0, 0],
    [0, 1],
    [0, 2],
    [1, 1],
  ],
  S: [
    [0, 1],
    [0, 2],
    [1, 0],
    [1, 1],
  ],
};

function normalize(cells: Cell[]): string {
  const top = Math.min(...cells.map(([r]) => r));
  const left = Math.min(...cells.map(([, c]) => c));
  return cells
    .map(([r, c]): Cell => [r - top, c - left])
    .sort((a, b) => a[0] - b[0] || a[1] - b[1])
    .map(([r, c]) => `${r},${c}`)
    .join(";");
}

/** Every rotation and reflection of every letter, keyed by its normalised cells. */
const SHAPES = new Map<string, Shape>();
for (const [name, base] of Object.entries(BASE) as [Shape, Cell[]][]) {
  let current = base;
  for (let i = 0; i < 4; i += 1) {
    current = current.map(([r, c]): Cell => [c, -r]);
    SHAPES.set(normalize(current), name);
    SHAPES.set(normalize(current.map(([r, c]): Cell => [r, -c])), name);
  }
}

/** `L`, `I`, `T` or `S` for four cells forming that tetromino, else null (the square too). */
export function shapeOf(cells: Cell[]): Shape | null {
  if (cells.length !== PIECE_SIZE) return null;
  return SHAPES.get(normalize(cells)) ?? null;
}

export function key([r, c]: Cell): string {
  return `${r},${c}`;
}

export function neighbours(r: number, c: number, rows: number, cols: number): Cell[] {
  const out: Cell[] = [];
  for (const [dr, dc] of [
    [-1, 0],
    [1, 0],
    [0, -1],
    [0, 1],
  ]) {
    const nr = r + dr;
    const nc = c + dc;
    if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) out.push([nr, nc]);
  }
  return out;
}

export function gridFits(grid: string[], rows: number, cols: number): boolean {
  return grid.length === rows && grid.every((line) => line.length === cols);
}

/** The region index of every cell, regions numbered in reading order of their first cell. */
export function regionMap(regions: string[]): number[][] {
  const rows = regions.length;
  const cols = regions[0]?.length ?? 0;
  const index = Array.from({ length: rows }, () => Array<number>(cols).fill(-1));
  let count = 0;
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      if (index[r][c] !== -1) continue;
      const char = regions[r][c];
      index[r][c] = count;
      const stack: Cell[] = [[r, c]];
      while (stack.length > 0) {
        const [cr, cc] = stack.pop() as Cell;
        for (const [nr, nc] of neighbours(cr, cc, rows, cols)) {
          if (index[nr][nc] === -1 && regions[nr]?.[nc] === char) {
            index[nr][nc] = count;
            stack.push([nr, nc]);
          }
        }
      }
      count += 1;
    }
  }
  return index;
}

/** Every region's cells in reading order; a region's first cell is its anchor. */
export function regionCells(regions: string[]): Cell[][] {
  const groups: Cell[][] = [];
  regionMap(regions).forEach((line, r) =>
    line.forEach((k, c) => {
      (groups[k] ??= []).push([r, c]);
    }),
  );
  return groups;
}

/** Orthogonally connected groups of `cells` (keys `r,c`), each in reading order. */
export function components(cells: Set<string>, rows: number, cols: number): Cell[][] {
  const seen = new Set<string>();
  const found: Cell[][] = [];
  const sorted = [...cells]
    .map((k) => k.split(",").map(Number) as Cell)
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  for (const start of sorted) {
    if (seen.has(key(start))) continue;
    seen.add(key(start));
    const group: Cell[] = [];
    const stack = [start];
    while (stack.length > 0) {
      const cell = stack.pop() as Cell;
      group.push(cell);
      for (const n of neighbours(cell[0], cell[1], rows, cols)) {
        if (cells.has(key(n)) && !seen.has(key(n))) {
          seen.add(key(n));
          stack.push(n);
        }
      }
    }
    found.push(group.sort((a, b) => a[0] - b[0] || a[1] - b[1]));
  }
  return found;
}

/** Top-left cell of every 2×2 block of shaded cells. */
export function pools(shaded: Set<string>, rows: number, cols: number): Cell[] {
  const found: Cell[] = [];
  for (let r = 0; r < rows - 1; r += 1) {
    for (let c = 0; c < cols - 1; c += 1) {
      if (
        shaded.has(`${r},${c}`) &&
        shaded.has(`${r + 1},${c}`) &&
        shaded.has(`${r},${c + 1}`) &&
        shaded.has(`${r + 1},${c + 1}`)
      )
        found.push([r, c]);
    }
  }
  return found;
}

export function cellsWith(grid: string[], char: string): Set<string> {
  const found = new Set<string>();
  grid.forEach((line, r) => {
    for (let c = 0; c < line.length; c += 1) if (line[c] === char) found.add(`${r},${c}`);
  });
  return found;
}

function touching(a: Cell[], b: Cell[], rows: number, cols: number): boolean {
  const other = new Set(b.map(key));
  return a.some(([r, c]) => neighbours(r, c, rows, cols).some((n) => other.has(key(n))));
}

export type Issue =
  "region_count" | "not_tetromino" | "same_shapes_touch" | "pool" | "disconnected";

/** Every rule a complete shading breaks, in the server's order. Empty when solved. */
export function shadingIssues(regions: string[], shading: string[]): Issue[] {
  const rows = regions.length;
  const cols = regions[0]?.length ?? 0;
  const shaded = cellsWith(shading, SHADED);
  const issues: Issue[] = [];
  const pieces: { cells: Cell[]; shape: Shape | null }[] = [];
  let counted = false;
  let split = false;
  for (const cells of regionCells(regions)) {
    const mine = cells.filter((cell) => shaded.has(key(cell)));
    if (mine.length !== PIECE_SIZE) {
      counted = true;
      continue;
    }
    if (components(new Set(mine.map(key)), rows, cols).length !== 1) {
      split = true;
      continue;
    }
    pieces.push({ cells: mine, shape: shapeOf(mine) });
  }
  if (counted) issues.push("region_count");
  if (split) issues.push("not_tetromino");
  const twins = pieces.some((a, i) =>
    pieces
      .slice(i + 1)
      .some(
        (b) => a.shape !== null && a.shape === b.shape && touching(a.cells, b.cells, rows, cols),
      ),
  );
  if (twins) issues.push("same_shapes_touch");
  if (pools(shaded, rows, cols).length > 0) issues.push("pool");
  if (components(shaded, rows, cols).length > 1) issues.push("disconnected");
  return issues;
}

export interface Contradictions {
  /** Anchors of the regions that can no longer hold a legal tetromino. */
  regions: Cell[];
  /** Top-left cells of the shaded 2×2 blocks. */
  pools: Cell[];
  /** The shaded cells can no longer be joined through cells that are not dotted. */
  disconnected: boolean;
}

/**
 * What a partial grid already gets wrong, whatever is played next. Sound: it says nothing it
 * is not sure of, and it never reads the answer key (CLAUDE.md §10).
 */
export function contradictions(regions: string[], progress: string[]): Contradictions {
  const rows = regions.length;
  const cols = regions[0]?.length ?? 0;
  const shaded = cellsWith(progress, SHADED);
  const dotted = cellsWith(progress, DOT);
  const flagged = new Set<string>();
  const complete: { anchor: Cell; cells: Cell[]; shape: Shape }[] = [];
  for (const cells of regionCells(regions)) {
    const anchor = cells[0];
    const mine = cells.filter((cell) => shaded.has(key(cell)));
    if (mine.length > PIECE_SIZE) {
      flagged.add(key(anchor));
      continue;
    }
    // The region's tetromino lies inside it and off the dots, and holds every shaded cell of
    // the region: some group of at least four open cells must contain them all.
    const open = new Set(cells.filter((cell) => !dotted.has(key(cell))).map(key));
    const room = components(open, rows, cols).some(
      (group) =>
        group.length >= PIECE_SIZE && mine.every((cell) => group.some((g) => key(g) === key(cell))),
    );
    if (!room) {
      flagged.add(key(anchor));
      continue;
    }
    if (mine.length === PIECE_SIZE) {
      const shape = shapeOf(mine);
      if (shape !== null) complete.push({ anchor, cells: mine, shape });
    }
  }
  complete.forEach((a, i) => {
    for (const b of complete.slice(i + 1)) {
      if (a.shape === b.shape && touching(a.cells, b.cells, rows, cols)) {
        flagged.add(key(a.anchor));
        flagged.add(key(b.anchor));
      }
    }
  });
  const open = new Set<string>();
  for (let r = 0; r < rows; r += 1)
    for (let c = 0; c < cols; c += 1) if (!dotted.has(`${r},${c}`)) open.add(`${r},${c}`);
  const reached = components(open, rows, cols).filter((group) =>
    group.some((cell) => shaded.has(key(cell))),
  );
  return {
    regions: [...flagged]
      .map((k) => k.split(",").map(Number) as Cell)
      .sort((a, b) => a[0] - b[0] || a[1] - b[1]),
    pools: pools(shaded, rows, cols),
    disconnected: reached.length > 1,
  };
}
